import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { forumGetTopics, forumMe, forumCreatePost } from "../lib/api";

const FORUM_TOKEN_KEY = "rl_forum_token_v1";

export default function ForumCreatePost() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const topicFromQuery = searchParams.get("topic") || "recovery_help";

  const [token, setToken] = React.useState(() => localStorage.getItem(FORUM_TOKEN_KEY) || "");
  const [user, setUser] = React.useState(null);
  const [topics, setTopics] = React.useState([]);
  const [topic, setTopic] = React.useState(topicFromQuery);
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [limits, setLimits] = React.useState({ postsRemainingToday: 0, postsPerDay: 3 });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setTopic(topicFromQuery);
  }, [topicFromQuery]);

  React.useEffect(() => {
    const run = async () => {
      if (!token) {
        nav("/forum", { replace: true });
        return;
      }
      setLoading(true);
      try {
        const [topicsData, meData] = await Promise.all([forumGetTopics(), forumMe(token)]);
        const allTopics = Object.values(topicsData?.groups || {}).flat();
        setTopics(allTopics);
        setUser(meData?.user || null);
        setLimits((prev) => ({ ...prev, ...(meData?.limits || {}) }));
        setError("");
      } catch (e) {
        localStorage.removeItem(FORUM_TOKEN_KEY);
        setToken("");
        nav("/forum", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token, nav]);

  const selectedTopic = React.useMemo(() => topics.find((t) => t.slug === topic) || null, [topics, topic]);
  const isAdminOnlyTopic = Boolean(selectedTopic?.adminOnlyPosting);
  const canCreate = !isAdminOnlyTopic || Boolean(user?.isOwner);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token || !canCreate) return;
    setSaving(true);
    setError("");
    try {
      await forumCreatePost({ token, topic, title, body });
      nav(`/forum?topic=${encodeURIComponent(topic)}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create post.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10" data-testid="forum-create-page">
      <Card className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle data-testid="forum-create-title">Create Post</CardTitle>
            <Button variant="outline" onClick={() => nav(`/forum?topic=${encodeURIComponent(topic)}`)} data-testid="forum-create-back-button">Back to Forum</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <>
              {!!error && <div className="text-sm text-red-600" data-testid="forum-create-error">{error}</div>}
              <div className="text-sm text-slate-700 dark:text-slate-300" data-testid="forum-create-limit-note">
                Posts left today: <span className="font-semibold">{limits.postsRemainingToday}</span>
              </div>

              <form onSubmit={onSubmit} className="space-y-3" data-testid="forum-create-form">
                <div>
                  <label className="text-sm font-medium">Area</label>
                  <select className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm" value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="forum-create-topic-select">
                    {topics.map((t) => (
                      <option key={t.slug} value={t.slug}>{t.label}{t.adminOnlyPosting ? " (Admin post only)" : ""}</option>
                    ))}
                  </select>
                </div>

                {isAdminOnlyTopic && !user?.isOwner && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200" data-testid="forum-create-admin-only-warning">
                    This area allows only admin posts. You can still comment on posts there.
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">Title</label>
                  <input className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="forum-create-title-input" />
                </div>

                <div>
                  <label className="text-sm font-medium">Body</label>
                  <textarea className="mt-1 w-full min-h-[180px] rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm" value={body} onChange={(e) => setBody(e.target.value)} data-testid="forum-create-body-input" />
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={!canCreate || saving} data-testid="forum-create-submit-button">{saving ? "Posting..." : "Publish Post"}</Button>
                </div>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
